import { Fragment, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { t } from "../i18n/t";
import { cx } from "./klasy";
import { IkonaAlarmy, IkonaUstawienia, IkonaWDol, IkonaWiecej, IkonaSzukaj, IkonaWLewo, IkonaWPrawo } from "./ikony";
import type { KomponentIkony } from "./ikony";
import { Okno } from "./Okno";
import { Menu, type PozycjaMenu } from "./Menu";
import { PrzyciskIkonaLink } from "./Przyciski";
import { Awatar, Pigulka, type RodzajPigulki } from "./Karta";
import "../styles/wspolne.css";

/* ---------- Banery ---------- */
/** Pasek banerów (telefon - przewijany w bok): nowy zestaw zaczyna się od pierwszego banera - baner doczytany później
 *  i wstawiony na początek nie zostaje schowany za lewą krawędzią (przyklejanie przewijania trzymało poprzedni). */
function PasekBanerow({ banery }: { banery: ReadonlyArray<BanerDane> }) {
  const pasek = useRef<HTMLElement>(null);
  const zestaw = banery.map((b) => b.id).join("|");
  useEffect(() => { pasek.current?.scrollTo({ left: 0 }); }, [zestaw]);
  return (
    <section ref={pasek} className="iot-banery" aria-label={t("Sprawy, które czekają na Ciebie")}>
      {banery.map((b) => <Baner key={b.id} {...b} />)}
    </section>
  );
}

/* ---------- Logo ---------- */
export function Logo({ className }: { className?: string }) {
  const id = useId();
  return (
    <svg className={cx("iot-marka__logo", className)} viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" style={{ stopColor: "var(--iot-wedzenie)" }} />
          <stop offset="1" style={{ stopColor: "var(--iot-destylacja)" }} />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="11" style={{ fill: "var(--iot-pow-2)" }} />
      <path d="M17 9a3 3 0 0 1 6 0v12.2a6 6 0 1 1-6 0z" fill="none" stroke={`url(#${id})`} strokeWidth="2.6" />
      <circle cx="20" cy="26" r="2.6" fill={`url(#${id})`} />
      <path d="M27.5 12.5a6 6 0 0 1 0 7M30.8 9.6a10.5 10.5 0 0 1 0 12.8" fill="none" style={{ stroke: "var(--iot-destylacja)" }} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

export function Marka({ do: cel }: { do: string }) {
  return (
    <Link className="iot-marka" to={cel} aria-label={t("termometrwifi - pulpit")}>
      <Logo />
      <span className="iot-marka__nazwa">termometr<b>wifi</b></span>
    </Link>
  );
}

/* ---------- Nawigacja ---------- */
export interface PozycjaNawigacji {
  id: string;
  etykieta: string;
  /** Krótsza etykieta do dolnego paska na telefonie */
  etykietaKrotka?: string;
  do: string;
  ikona: KomponentIkony;
  licznik?: number;
  /** Bieżący ekran należy do tej pozycji (APP liczy sama - „Pulpit” obejmuje też pulpity własne) */
  aktywna: boolean;
}

export interface SekcjaNawigacji {
  id: string;
  tytul: string;
  pozycje: ReadonlyArray<PozycjaNawigacji>;
}

function LinkNawigacji({ p, krotka, onClick }: { p: PozycjaNawigacji; krotka?: boolean; onClick?: () => void }) {
  return (
    <Link to={p.do} className={cx("iot-nav-link", p.aktywna && "is-active")} aria-current={p.aktywna ? "page" : undefined} onClick={onClick}>
      <p.ikona />
      <span className="iot-nav-link__etykieta">{krotka ? p.etykietaKrotka ?? p.etykieta : p.etykieta}</span>
      {p.licznik !== undefined && p.licznik > 0 && (
        <span className="iot-licznik"><span className="iot-sr">{t("nowe:")} </span>{p.licznik > 99 ? "99+" : p.licznik}</span>
      )}
    </Link>
  );
}

/* ---------- Baner „coś czeka” ---------- */
export type RodzajBanera = "info" | "uwaga" | "nowe";

export interface BanerDane {
  id: string;
  rodzaj: RodzajBanera;
  ikona: KomponentIkony;
  /** Treść z pogrubieniami (np. <b>{nazwa}</b>) */
  tresc: ReactNode;
  etykietaAkcji: string;
  onAkcja: () => void;
}

export function Baner({ rodzaj, ikona: Ikona, tresc, etykietaAkcji, onAkcja }: BanerDane) {
  return (
    <div className={cx("iot-baner", `iot-baner--${rodzaj}`)} role="status">
      <Ikona className="iot-baner__ikona" />
      <p>{tresc}</p>
      <div className="iot-baner__akcje"><button type="button" className="iot-btn iot-btn--jasny iot-btn--maly" onClick={onAkcja}>{etykietaAkcji}</button></div>
    </div>
  );
}

/* ---------- Powłoka aplikacji ---------- */
export interface UzytkownikPowloki {
  imie: string;
  pelneImie: string;
  email: string;
  inicjaly: string;
  zdjecieUrl?: string;
}

/** Powłoka APP i panelu admina (docs/admin/PROJEKT.md 0.2): dzwonek, szukanie, Ustawienia i linki prawne są
 *  opcjonalne - brak grupy = brak elementu. */
export interface PowlokaProps {
  nawigacja: ReadonlyArray<SekcjaNawigacji>;
  /** id pozycji widocznych w dolnym pasku telefonu (maks. 4 - piąta to „Więcej”) */
  dolnyPasek: ReadonlyArray<string>;
  uzytkownik: UzytkownikPowloki;
  /** Adres pod logo */
  pulpit: string;
  /** Dzwonek alarmów w pasku górnym */
  alarmy?: { do: string; liczba: number };
  /** Np. „3 z 4 online” - klik prowadzi do listy urządzeń */
  stanUrzadzen?: { tekst: string; rodzaj: RodzajPigulki; do: string };
  /** Pole szukania w pasku górnym; `wyniki` - lista pod polem (pokazuje aplikacja) */
  szukaj?: { tekst: string; onZmien: (tekst: string) => void; podpowiedz: string; wyniki?: ReactNode };
  /** Pozycje menu konta (awatar w pasku) */
  menuKonta: ReadonlyArray<PozycjaMenu>;
  menuWiecejOtwarte: boolean;
  onMenuWiecej: (otwarte: boolean) => void;
  /** Tytuł arkusza „Więcej” na telefonie */
  tytulWiecej: string;
  /** Na dole menu bocznego i arkusza „Więcej” (np. instalacja aplikacji) - funkcja dostaje miejsce */
  stopkaMenu?: (miejsce: "menu" | "wiecej") => ReactNode;
  /** Stała pozycja „Ustawienia” w stopce menu i w „Więcej” */
  ustawienia?: { do: string; aktywne: boolean };
  /** Linki prawne pod menu */
  prawne?: { polityka: string; regulamin: string };
  banery?: ReadonlyArray<BanerDane>;
  /** Nad treścią: pasek braku sieci, wskaźnik odświeżania */
  nadTrescia?: ReactNode;
  /** Tost u dołu ekranu */
  tost?: ReactNode;
  children: ReactNode;
}

function Prawne({ adresy }: { adresy: PowlokaProps["prawne"] }) {
  if (!adresy) return null;
  return (
    <p className="iot-nawigacja__prawne">
      <a href={adresy.polityka} target="_blank" rel="noopener">{t("Polityka prywatności")}</a>
      {" · "}<a href={adresy.regulamin} target="_blank" rel="noopener">{t("Regulamin")}</a>
    </p>
  );
}

/** Zwinięte menu boczne na komputerze - tylko w tej przeglądarce (wygoda, nie ustawienie konta). */
const KLUCZ_MENU = "iot-menu-zwiniete";
const czytajZwiniete = () => { try { return localStorage.getItem(KLUCZ_MENU) === "1"; } catch { return false; } };
const zapiszZwiniete = (z: boolean) => { try { localStorage.setItem(KLUCZ_MENU, z ? "1" : "0"); } catch { /* bez pamięci - do zamknięcia karty */ } };

/**
 * Powłoka: menu boczne (tablet - szyna ikon, komputer - pełne albo zwinięte do szyny), pasek górny, banery,
 * dolny pasek na telefonie i arkusz „Więcej”. Układ przełącza CSS (media queries + `.is-menu-zwiniete`).
 */
/** Kafel menu „Więcej” (telefon). */
function KafelWiecej({ p, zamknij }: { p: PozycjaNawigacji; zamknij: () => void }) {
  return (
    <Link to={p.do} className={cx("iot-wiecej__kafel", p.aktywna && "is-active")} aria-current={p.aktywna ? "page" : undefined} onClick={zamknij}>
      <p.ikona /><span>{p.etykieta}</span>
    </Link>
  );
}

export function Powloka({ nawigacja, dolnyPasek, uzytkownik, pulpit, alarmy, stanUrzadzen, szukaj, menuKonta, menuWiecejOtwarte, onMenuWiecej, tytulWiecej, stopkaMenu, ustawienia: u, prawne, banery, nadTrescia, tost, children }: PowlokaProps) {
  const idSzukaj = useId();
  const wszystkie = nawigacja.flatMap((s) => s.pozycje);
  const dolne = dolnyPasek.map((id) => wszystkie.find((p) => p.id === id)).filter((p): p is PozycjaNawigacji => p !== undefined);
  const wWiecej = !!u?.aktywne || wszystkie.some((p) => p.aktywna && !dolnyPasek.includes(p.id));
  const ustawienia: PozycjaNawigacji | null = u ? { id: "ustawienia", etykieta: t("Ustawienia"), do: u.do, ikona: IkonaUstawienia, aktywna: u.aktywne } : null;
  const [zwiniete, ustawZwiniete] = useState(czytajZwiniete);
  const przelaczMenu = () => { zapiszZwiniete(!zwiniete); ustawZwiniete(!zwiniete); };

  return (
    <div className={cx("iot-app", zwiniete && "is-menu-zwiniete")}>
      <a className="iot-pomin" href="#iot-tresc">{t("Przejdź do treści")}</a>

      <aside className="iot-nawigacja" aria-label={t("Menu główne")}>
        <button type="button" className="iot-nawigacja__zwin" onClick={przelaczMenu} aria-expanded={!zwiniete}
          aria-label={zwiniete ? t("Rozwiń menu") : t("Zwiń menu")} title={zwiniete ? t("Rozwiń menu") : t("Zwiń menu")} data-test="zwin-menu">
          {zwiniete ? <IkonaWPrawo /> : <IkonaWLewo />}
        </button>
        <Marka do={pulpit} />
        <nav className="iot-nawigacja__lista" aria-label={t("Menu główne")}>
          {nawigacja.map((s) => (
            <div key={s.id} className="iot-nawigacja__grupa" role="group" aria-label={s.tytul}>
              <p className="iot-nawigacja__sekcja" aria-hidden="true">{s.tytul}</p>
              {s.pozycje.map((p) => <LinkNawigacji key={p.id} p={p} />)}
            </div>
          ))}
        </nav>
        <div className="iot-nawigacja__stopka">
          {ustawienia && <LinkNawigacji p={ustawienia} />}
          {stopkaMenu?.("menu")}
          <Prawne adresy={prawne} />
        </div>
      </aside>

      <div className="iot-app__glowna">
        <header className="iot-pasek-gorny">
          <Marka do={pulpit} />
          {szukaj && (
            <div className="iot-pasek-gorny__szukaj iot-szukaj">
              <label htmlFor={idSzukaj} className="iot-sr">{t("Szukaj")}</label>
              <IkonaSzukaj />
              <input id={idSzukaj} type="search" className="iot-wejscie" value={szukaj.tekst} onChange={(e) => szukaj.onZmien(e.target.value)}
                placeholder={szukaj.podpowiedz} autoComplete="off"
                onKeyDown={(e) => { if (e.key === "Escape") szukaj.onZmien(""); }} />
              {szukaj.wyniki}
            </div>
          )}
          <div className="iot-pasek-gorny__akcje">
            {stanUrzadzen && (
              <Link to={stanUrzadzen.do} className="iot-pasek-gorny__stan" data-test="stan-urzadzen">
                <Pigulka rodzaj={stanUrzadzen.rodzaj} kropka>{stanUrzadzen.tekst}</Pigulka>
              </Link>
            )}
            {alarmy && <PrzyciskIkonaLink do={alarmy.do} ikona={IkonaAlarmy} etykieta={t("Alarmy")} licznik={alarmy.liczba} />}
            <Menu
              wyzwalacz={
                <button type="button" className="iot-konto" aria-label={t("Twoje konto")} data-test="konto">
                  <Awatar inicjaly={uzytkownik.inicjaly} zdjecieUrl={uzytkownik.zdjecieUrl} />
                  <span className="iot-pasek-gorny__imie">{uzytkownik.imie}</span>
                  <IkonaWDol className="iot-pasek-gorny__imie" />
                </button>
              }
              naglowek={<><b>{uzytkownik.pelneImie}</b><br />{uzytkownik.email}</>}
              pozycje={menuKonta}
            />
          </div>
        </header>

        {nadTrescia}

        {banery && banery.length > 0 && <PasekBanerow banery={banery} />}

        <main id="iot-tresc" className="iot-app__tresc">{children}</main>
      </div>

      <nav className="iot-dolny-pasek" aria-label={t("Menu główne")}>
        {dolne.map((p) => <LinkNawigacji key={p.id} p={p} krotka />)}
        <button type="button" className={cx("iot-nav-link", wWiecej && "is-active")} onClick={() => onMenuWiecej(true)} aria-haspopup="dialog">
          <IkonaWiecej /><span className="iot-nav-link__etykieta">{t("Więcej")}</span>
        </button>
      </nav>

      <Okno otwarte={menuWiecejOtwarte} onZmianaOtwarcia={onMenuWiecej} tytul={tytulWiecej}>
        {/* W tych samych grupach co menu boczne, z nagłówkami - przy wielu pozycjach płaska siatka była nie do ogarnięcia. */}
        <nav className="iot-wiecej" aria-label={tytulWiecej}>
          {nawigacja.map((s) => (
            <Fragment key={s.id}>
              <p className="iot-wiecej__sekcja">{s.tytul}</p>
              {s.pozycje.map((p) => <KafelWiecej key={p.id} p={p} zamknij={() => onMenuWiecej(false)} />)}
            </Fragment>
          ))}
          {ustawienia && <KafelWiecej p={ustawienia} zamknij={() => onMenuWiecej(false)} />}
          {stopkaMenu?.("wiecej")}
        </nav>
        <Prawne adresy={prawne} />
      </Okno>

      {tost}
    </div>
  );
}
